import {tokens} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Footer from '@/components/footer'
import Header from '@/components/header'
import InlineWistiaVideo from '@/components/inline-wistia-video'
import SEO from '@/components/seo'
import type {ResourceDetailPageQuery} from '@/graphql-types'
import {VideoLibrary} from '@/sections/video-library'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<ResourceDetailPageQuery>

const ResourceDetailPage = ({data}: Props) => {
    const content = data.prismicResourceDetailPage.data

    const showMoreVideos = data.allPrismicResourceDetailPage.nodes.map((node) => ({
        ...node.data,
        uid: node.uid,
    }))

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />
            <Main>
                <StyledSection>
                    <HeaderWrapper>
                        <Text variant="paragraph-large" color="brand">
                            {content.category}
                        </Text>

                        <Text as="h1" variant="heading-large">
                            {content.title}
                        </Text>

                        <Description color="muted">{content.description}</Description>
                    </HeaderWrapper>
                    <div>
                        <InlineWistiaVideo
                            videoId={content.wistia_video_id}
                            thumbnail={content.thumbnail}
                            onRequestVideo={() =>
                                content.tracking_id &&
                                track(TrackingEvent.ResourceRequested, {
                                    value: content.tracking_id,
                                })
                            }
                        />
                    </div>
                </StyledSection>
                <VideoLibrary title={content.more_videos_section_title} items={showMoreVideos} />
            </Main>
            <Footer />
        </>
    )
}

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        gap: ${tokens.spacing40};
    }
`

const HeaderWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing12};
    grid-template-columns: 1fr;

    ${mq.min('sm')} {
        grid-template-rows: auto auto;
        grid-template-columns: 1fr 1fr;
        grid-auto-flow: column;
        gap: ${tokens.spacing12} ${tokens.spacing32};
    }
`

const Description = styled(Text)`
    margin-top: ${tokens.spacing8};

    ${mq.min('sm')} {
        margin-top: unset;
        grid-column: 2;
        grid-row: 2;
    }
`

export const Head = ({location, pageContext, data}: GatsbyHeadProps<ResourceDetailPageQuery>) => {
    const pageData = data.prismicResourceDetailPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query ResourceDetailPage(
        $uid: String!
        $locale: String!
        $imageBreakpoints: [Int]
        $threeColumns: String
    ) {
        prismicResourceDetailPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            id
            uid
            lang
            type
            last_publication_date
            alternate_languages {
                uid
                lang
                type
            }
            data {
                category
                description
                duration
                thumbnail {
                    alt
                    copyright
                    dimensions {
                        height
                        width
                    }
                    url
                    gatsbyImageData(
                        breakpoints: $imageBreakpoints
                        sizes: "(max-width: 1440px) 100vw, 1240px"
                    )
                }
                title
                wistia_video_id
                tracking_id
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                more_videos_section_title
            }
        }

        allPrismicResourceDetailPage(limit: 3, filter: {uid: {ne: $uid}, lang: {eq: $locale}}) {
            nodes {
                uid
                data {
                    title
                    description
                    category
                    duration
                    thumbnail {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(ResourceDetailPage)
